import { z } from "zod";
import { api } from "./client";

/* ──────────────────────────────────────────────────────────────────────
   Schemas mirror api/models/issues.py
   ────────────────────────────────────────────────────────────────────── */

export const PublisherSummary = z.object({
  channel_id: z.number().int(),
  channel_name: z.string(),
  category_id: z.number().int().nullable(),
  category:    z.string().nullable(),    // 'Regional' | 'Mainline' | 'Financial' | 'Periodical'
  language_id: z.number().int().nullable(),
  language:    z.string().nullable(),
  edition_count: z.number().int(),
  crawlable_count: z.number().int(),
  needs_review_count: z.number().int(),
  latest_business_date: z.string().nullable(),
  latest_page_count: z.number().int().nullable(),
  today_issue_count: z.number().int(),
});
export type PublisherSummary = z.infer<typeof PublisherSummary>;

export const EditionSummary = z.object({
  edition_id: z.number().int(),
  channel_id: z.number().int(),
  channel_name: z.string(),
  city: z.string().nullable(),
  edition_name: z.string().nullable(),
  state: z.string().nullable(),
  zone: z.string().nullable(),
  language: z.string().nullable(),
  circulation: z.number().int().nullable(),
  readership: z.number().int().nullable(),
  is_crawlable: z.boolean(),
  is_scraped: z.boolean(),
  typical_publish_time_local: z.string().nullable(),
  learned_publish_time_local: z.string().nullable(),
  last_issue_date: z.string().nullable(),
  last_issue_pages: z.number().int().nullable(),
  last_issue_bytes: z.number().int().nullable(),
  last_issue_fetched_at: z.string().nullable().optional(),
});
export type EditionSummary = z.infer<typeof EditionSummary>;

export const IssueListItem = z.object({
  issue_id: z.number().int(),
  edition_id: z.number().int(),
  business_date: z.string(),
  page_count: z.number().int(),
  status: z.string(),
  output_root: z.string().nullable(),
  total_bytes: z.number().int(),
});
export type IssueListItem = z.infer<typeof IssueListItem>;

export const PageInfo = z.object({
  artifact_id: z.number().int(),
  page_number: z.number().int().nullable(),
  display_page: z.string().nullable(),
  file_size: z.number().int(),
  mime_type: z.string().nullable(),
  file_path_full: z.string().nullable(),
  image_url: z.string().nullable(),
  proxy_url: z.string(),
});
export type PageInfo = z.infer<typeof PageInfo>;

export const IssueDetail = z.object({
  issue_id: z.number().int(),
  edition_id: z.number().int(),
  business_date: z.string(),
  channel_id: z.number().int(),
  channel_name: z.string(),
  city: z.string().nullable(),
  edition_name: z.string().nullable(),
  page_count: z.number().int(),
  status: z.string(),
  pages: z.array(PageInfo),
});
export type IssueDetail = z.infer<typeof IssueDetail>;

export const TodayDashboard = z.object({
  business_date: z.string(),
  latest_available_date: z.string().nullable(),
  total_issues: z.number().int(),
  total_pages: z.number().int(),
  total_bytes: z.number().int(),
  publishers_today: z.number().int(),
  display_date: z.string(),
  is_today: z.boolean(),
  by_publisher: z.array(z.object({
    channel_name: z.string(),
    issue_count: z.number().int(),
    page_count: z.number().int(),
    bytes: z.number().int(),
    /** UTC ISO timestamp — earliest crawler fetch for the display date. */
    first_fetched_at: z.string().nullable().optional(),
  })),
});
export type TodayDashboard = z.infer<typeof TodayDashboard>;

/* ──────────────────────────────────────────────────────────────────────
   Calls
   ────────────────────────────────────────────────────────────────────── */

export async function fetchDashboard(businessDate?: string): Promise<TodayDashboard> {
  const params = businessDate ? `?business_date=${businessDate}` : "";
  const r = await api.get(`/api/dashboard/today${params}`);
  return TodayDashboard.parse(r.data);
}

export interface FetchPublishersOpts {
  includeUncrawled?: boolean;
  businessDate?: string;        // YYYY-MM-DD
}

export async function fetchPublishers(
  opts: FetchPublishersOpts = {},
): Promise<PublisherSummary[]> {
  const params = new URLSearchParams();
  if (opts.includeUncrawled) params.set("include_uncrawled", "true");
  if (opts.businessDate)     params.set("business_date", opts.businessDate);
  const qs = params.toString();
  const r = await api.get(`/api/publishers${qs ? `?${qs}` : ""}`);
  return z.array(PublisherSummary).parse(r.data);
}

export async function fetchEditions(channelId: number): Promise<EditionSummary[]> {
  const r = await api.get(`/api/publishers/${channelId}/editions`);
  return z.array(EditionSummary).parse(r.data);
}

export async function fetchIssues(
  editionId: number,
  opts: { dateFrom?: string; dateTo?: string; limit?: number } = {},
): Promise<IssueListItem[]> {
  const params = new URLSearchParams();
  if (opts.dateFrom) params.set("date_from", opts.dateFrom);
  if (opts.dateTo) params.set("date_to", opts.dateTo);
  if (opts.limit) params.set("limit", String(opts.limit));
  const q = params.toString();
  const r = await api.get(`/api/editions/${editionId}/issues${q ? `?${q}` : ""}`);
  return z.array(IssueListItem).parse(r.data);
}

export async function fetchIssueDetail(issueId: number): Promise<IssueDetail> {
  const r = await api.get(`/api/issues/${issueId}`);
  return IssueDetail.parse(r.data);
}

/* ──────────────────────────────────────────────────────────────────────
   Article zones — the publisher's own "click a story to read it" layer.
   Fetched per page, not with the issue: only the page being viewed needs them.
   ────────────────────────────────────────────────────────────────────── */

export const ArticleZone = z.object({
  article_id: z.number().int(),
  article_no: z.number().int(),
  bbox_x: z.number().int(),
  bbox_y: z.number().int(),
  bbox_w: z.number().int(),
  bbox_h: z.number().int(),
  position: z.string().nullable(),
  headline: z.string().nullable(),
  kind: z.string().nullable(),
  crop_url: z.string(),
  crop_w: z.number().int().nullable(),
  crop_h: z.number().int().nullable(),
});
export type ArticleZone = z.infer<typeof ArticleZone>;

export const PageArticles = z.object({
  artifact_id: z.number().int(),
  page_w: z.number().int(),
  page_h: z.number().int(),
  source: z.string().nullable(),
  articles: z.array(ArticleZone),
});
export type PageArticles = z.infer<typeof PageArticles>;

export async function fetchPageArticles(artifactId: number): Promise<PageArticles> {
  const r = await api.get(`/api/pages/${artifactId}/articles`);
  return PageArticles.parse(r.data);
}

/* ──────────────────────────────────────────────────────────────────────
   Download suggestions — per-publisher / per-edition defaults.
   ────────────────────────────────────────────────────────────────────── */

export const DownloadSuggestion = z.object({
  channel_id: z.number().int(),
  edition_id: z.number().int().nullable(),
  suggested_width:  z.number().int().nullable(),
  suggested_height: z.number().int().nullable(),
  suggested_dpi:    z.number().int().nullable(),
  notes:            z.string().nullable(),
  scope:            z.enum(["edition", "publisher", "none"]),
  updated_at_utc:   z.string().nullable(),
  updated_by:       z.string().nullable(),
});
export type DownloadSuggestion = z.infer<typeof DownloadSuggestion>;

export async function fetchSuggestion(
  channelId: number, editionId?: number | null,
): Promise<DownloadSuggestion> {
  const q = editionId ? `?edition_id=${editionId}` : "";
  const r = await api.get(`/api/publishers/${channelId}/suggestion${q}`);
  return DownloadSuggestion.parse(r.data);
}

export interface SuggestionPut {
  edition_id?: number | null;
  suggested_width?: number | null;
  suggested_height?: number | null;
  suggested_dpi?: number | null;
  notes?: string | null;
}

export async function saveSuggestion(
  channelId: number, body: SuggestionPut,
): Promise<DownloadSuggestion> {
  const r = await api.put(`/api/publishers/${channelId}/suggestion`, body);
  return DownloadSuggestion.parse(r.data);
}
